import React from 'react';
import { CalendarDaysIcon, DiamondIcon, StarIcon, WrenchIcon } from 'lucide-react';
import { yearPlan } from '../data/content';
import { SectionHeading } from './SectionHeading';

/** One color per quarter (full class names so Tailwind keeps them). Dark text on yellow for contrast. */
const quarterStyles = [{
  band: 'bg-brand text-white',
  star: 'text-brand'
}, {
  band: 'bg-coral text-white',
  star: 'text-coral-ink'
}, {
  band: 'bg-violet text-white',
  star: 'text-violet-ink'
}, {
  band: 'bg-sun text-ink',
  star: 'text-ink'
}];

/** The 52-week plan, quarter by quarter: ★ complete weeks, mapped weeks and ◆ IEP data checks */
export function YearPlanSection() {
  return <section id="year" aria-labelledby="year-title" className="scroll-mt-4 bg-white py-12 md:py-20" data-id="element-310" data-mp="37blr9rstx3kc">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-311" data-mp="29wrardj2bvfk">
        <SectionHeading id="year-title" eyebrow={yearPlan.eyebrow} title={yearPlan.title} sub={yearPlan.sub} data-id="element-312" data-mp="29w7vk4ksrl58" />

        <ol className="mt-8 grid gap-4 md:mt-12 md:grid-cols-2 lg:grid-cols-4" data-id="element-313" data-mp="2ethvie5tbf7b">
          {yearPlan.quarters.map((q, qi) => {
          const s = quarterStyles[qi % quarterStyles.length];
          return <li key={q.q} className="flex flex-col overflow-hidden rounded-xl border border-hair bg-white" data-id="element-314" data-mp="1qjxy66v31a94" data-mpi={qi}>
                <div className={`flex items-baseline justify-between gap-3 px-5 py-3 ${s.band}`} data-id="element-315" data-mp="1ll34g9vku1a8">
                  <span className="font-display text-[1.6rem] font-black leading-none" data-id="element-316" data-mp="1gliakdv9kj4d">{q.q}</span>
                  <span className="font-display text-[12px] font-extrabold tracking-[0.1em]" data-id="element-317" data-mp="1bmx5a015n992">{q.weeks}</span>
                </div>
                <div className="flex flex-1 flex-col p-5" data-id="element-318" data-mp="17m3lniuqwglz">
                  <h3 className="font-display text-[16px] font-black tracking-[0.04em] text-ink" data-id="element-319" data-mp="12lz3loe6vdtn">{q.name}</h3>
                  <ul className="mt-4 space-y-3" data-id="element-320" data-mp="31zmnh4vt6ld3">
                    {q.full.map(m => {
                  const review = 'review' in m && m.review;
                  const mapped = 'mapped' in m && m.mapped;
                  const Icon = review ? DiamondIcon : mapped ? CalendarDaysIcon : StarIcon;
                  return <li key={`${q.q}-${m.week}`} className="flex gap-3" data-id="element-321" data-mp="3kwmru5vv8qo0">
                          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${mapped ? 'text-ink/45' : 'fill-current'} ${review ? 'text-leaf-ink' : mapped ? '' : s.star}`} aria-hidden="true" data-id="element-322" data-mp="37588tz1ykpgy" />
                          <div className="min-w-0" data-id="element-323" data-mp="7ojtkyakplfr">
                            <p className="text-[11px] font-bold tracking-[0.1em] text-ink/55" data-id="element-324" data-mp="7o0j22n95zbr">
                              WEEK {m.week} · {m.label}
                            </p>
                            <p className={`font-display text-[15px] font-bold leading-snug ${review ? 'text-leaf-ink' : 'text-brand'}`} data-id="element-325" data-mp="3us7yihbvqf43">{m.title}</p>
                          </div>
                        </li>;
                })}
                  </ul>
                  <p className="mt-auto pt-4 text-[14px] leading-snug text-ink/70" data-id="element-326" data-mp="2bu219hixdx99">{q.activities}</p>
                </div>
              </li>;
        })}
        </ol>

        <div className="mx-auto mt-6 max-w-[900px] rounded-xl bg-sun-soft p-5 md:mt-8 md:p-6" data-id="element-327" data-mp="2kv9maxwfl1le">
          <div className="flex items-center gap-3" data-id="element-328" data-mp="2fwf2bsmq96zp">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sun text-ink" data-id="element-329" data-mp="2fwycuoa1st3p">
              <WrenchIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" data-id="element-330" data-mp="1s7416a1gtal1" />
            </span>
            <h3 className="font-display text-[14px] font-black tracking-[0.08em] text-ink" data-id="element-331" data-mp="1o73lkm2mqtce">{yearPlan.toolsTitle}</h3>
          </div>
          <ul className="mt-4 grid gap-2.5 md:grid-cols-3 md:gap-4" data-id="element-332" data-mp="1x676lvlcxitj">
            {yearPlan.tools.map(t => <li key={t} className="rounded-lg bg-white px-4 py-3 text-[15px] font-medium leading-snug text-ink" data-id="element-333" data-mp="19fuo5qshk3op">
                {t}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}